import { isConfigApiError, type EntityDetailError } from '@web-config-app/core';
import { buildErrorSummaryParts } from '../build-error-summary-parts/build-error-summary-parts';

type FormatMessageFn = (
  descriptor: { id: string },
  values?: Record<string, string | number>,
) => string;

/**
 * Builds a structured {@link EntityDetailError} from an unknown error.
 *
 * For a `ConfigApiError`, uses the first entry in the `errors` array to
 * populate `detail` (the user-facing message) and `secondaryDetail`
 * (title / code / error ID summary). Falls back to `error.message` when
 * structured fields are missing.
 *
 * For generic `Error` instances, uses `error.message` as the detail.
 *
 * Always returns a valid `EntityDetailError` — uses the translatable
 * `NO_SERVICE_ERROR_DETAIL` fallback when no meaningful information
 * can be extracted.
 */
export const buildOperationErrorDetail = (
  error: unknown,
  formatMessage: FormatMessageFn,
): EntityDetailError => {
  const fallbackDetail = formatMessage({ id: 'NO_SERVICE_ERROR_DETAIL' });

  if (isConfigApiError(error)) {
    const firstError = error.errors?.[0];
    const detail =
      firstError?.detail?.trim() || error.message || fallbackDetail;
    const summaryParts =
      firstError && buildErrorSummaryParts(firstError, formatMessage);
    const trimmedMessage = error.message?.trim();
    const secondaryDetail = summaryParts || trimmedMessage || undefined;

    return { detail, secondaryDetail };
  }

  if (error instanceof Error && error.message) {
    return { detail: error.message };
  }

  return { detail: fallbackDetail };
};
